import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { X, Loader2, Camera, Gauge } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../Toast';

const kmSchema = z.object({
  km_value: z.number({ error: 'Informe a quilometragem' }).positive('Deve ser maior que 0'),
});

type KmFormData = z.infer<typeof kmSchema>;

interface KmSheetProps {
  journeyId: string;
  type: 'start' | 'end';
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function KmSheet({ journeyId, type, isOpen, onClose, onSaved }: KmSheetProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<KmFormData>({
    resolver: zodResolver(kmSchema),
  });

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
  };

  const uploadPhoto = async (file: File): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    return new Promise((resolve, reject) => {
      img.onload = async () => {
        const maxWidth = 1280;
        const scale = Math.min(1, maxWidth / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(img.src);

        canvas.toBlob(
          async (blob) => {
            if (!blob) return reject(new Error('Failed to compress'));
            const formData = new FormData();
            formData.append('photo', blob, `km_${Date.now()}.jpg`);

            try {
              const res = await api.post('/photos/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              resolve(res.data.data.url);
            } catch (err) {
              reject(err);
            }
          },
          'image/jpeg',
          0.7
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const mutation = useMutation({
    mutationFn: async (data: KmFormData) => {
      setIsUploading(true);
      let photo_url: string | undefined;

      if (photoFile) {
        photo_url = await uploadPhoto(photoFile);
      }

      const payload: Record<string, unknown> = {};
      if (type === 'start') {
        payload.km_start = data.km_value;
      } else {
        payload.km_end = data.km_value;
      }
      if (photo_url) {
        payload.photo_url = photo_url;
      }

      const res = await api.patch(`/journeys/${journeyId}/km`, payload);
      return res.data.data;
    },
    onSuccess: () => {
      setIsUploading(false);
      toast({ title: 'KM registrado com sucesso', variant: 'success' });
      onSaved();
      onClose();
    },
    onError: () => {
      setIsUploading(false);
      toast({ title: 'Erro ao registrar KM', variant: 'error' });
    },
  });

  const onSubmit = (data: KmFormData) => {
    mutation.mutate(data);
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl pb-safe animate-slide-up">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            {type === 'start' ? 'Registrar KM Inicio' : 'Registrar KM Final'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 active:bg-gray-100"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div className="text-center mb-2">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Gauge className="h-7 w-7 text-primary" />
            </div>
            <p className="text-sm text-gray-600">
              {type === 'start'
                ? 'Informe a quilometragem atual do odometro'
                : 'Informe a quilometragem final do odometro'}
            </p>
          </div>

          <div>
            <label htmlFor="km_value" className="block text-xs font-medium text-gray-600 mb-1">
              Quilometragem (KM)
            </label>
            <input
              id="km_value"
              type="number"
              inputMode="numeric"
              {...register('km_value', { valueAsNumber: true })}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                errors.km_value ? 'border-red-300' : 'border-gray-200'
              }`}
              placeholder="Ex: 45230"
            />
            {errors.km_value && (
              <p className="text-xs text-red-500 mt-1">{errors.km_value.message}</p>
            )}
          </div>

          {/* Photo capture */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Foto do Odometro (opcional)
            </label>
            {photoPreview ? (
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Odometro"
                  className="w-full h-32 object-cover rounded-xl border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPhotoPreview(null);
                    setPhotoFile(null);
                  }}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 active:bg-gray-50"
              >
                <Camera size={18} />
                Tirar foto do odometro
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              className="hidden"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSkip}
              className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium text-sm"
            >
              Pular
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || isUploading}
              className="flex-1 py-3 rounded-xl bg-primary text-white font-medium text-sm active:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {mutation.isPending || isUploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Salvando...
                </>
              ) : (
                'Confirmar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
